import { Button } from "@qn/ui";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportCrash } from "../lib/crash";
import { translate } from "../lib/i18n";
import { usePrefs } from "../lib/prefs";

/** A crash inside a page shows a calm recovery screen (reload, copy details) instead of a blank app. */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("ui crash", error, info.componentStack);
    reportCrash(`${error.name}: ${error.message}`);
  }
  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const locale = usePrefs.getState().locale;
    const t = (k: "crash.title" | "crash.body" | "crash.reload" | "crash.copy") =>
      translate(locale, k);
    const details = `${error.name}: ${error.message}\n${(error.stack ?? "").split("\n").slice(0, 6).join("\n")}`;
    return (
      <div
        role="alert"
        className="mx-auto flex h-full max-w-md flex-col items-center justify-center gap-4 p-6 text-center"
      >
        <h1 className="font-display text-2xl font-semibold">{t("crash.title")}</h1>
        <p className="text-sm text-[var(--fg-muted)]">{t("crash.body")}</p>
        <div className="flex gap-2">
          <Button variant="primary" onClick={() => location.reload()}>
            {t("crash.reload")}
          </Button>
          <Button variant="ghost" onClick={() => void navigator.clipboard?.writeText(details)}>
            {t("crash.copy")}
          </Button>
        </div>
      </div>
    );
  }
}
